import { Box, Stack, Typography } from '@mui/material';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { DragEvent, useState } from 'react';
import { TrackerDataType } from '../../shared/constants/constants';
import VerificationModal from '../VerificationModal/VerificationModal';
import { getQueryAdderUiPalette, queryAdderTypeScale } from '../../shared/constants/queryAdderPalette';
import { LOG_ADDER_CONFIG } from './logAdderConfig';
import {
  FieldLabel,
  QueryAdderFieldGridItem,
  QueryAdderModalShell,
  QueryAdderScopedStyles,
  QueryAdderStickyHeader,
  QueryAdderUploadDropzone,
  SectionCard,
  queryAdderWideTwoColumnGridSx,
  queryAdderTwoColumnGridSx,
  renderQueryAdderCell,
} from './QueryAdderLayoutShared';

const ui = getQueryAdderUiPalette(TrackerDataType.SERVICE);
const serviceAdderConfig = LOG_ADDER_CONFIG.service;

const infoText = {
  service:
    'Use a clear Topic that can also act as the shared Topic for linked Interactions, Engagements, and Content records.',
  type: 'Choose the Type or Types of Work completed for this Service so reporting and permissions stay accurate.',
  complexity: 'Set the effort level for this Service so workload and reporting can stay consistent across records.',
  units: 'Select the units connected to this Service request.',
  entryDate: 'Use Entry Date to mark when the task or work begins.',
  dueDate: 'Use Due Date for the target timeline, or as the completion date once the work is finished.',
  dept: 'Add the department when it helps narrow the Service context or ownership.',
  unitLead: 'Use Lead/Expert for the spokesperson, subject-matter expert, or client lead tied to this Service.',
  teamMember: 'Use Team Members for the staff doing the work so ownership and follow-up stay visible.',
  campaign: 'Use Campaign to group this Service under a larger initiative when relevant.',
  status: 'Set the current stage of this Service record.',
  file: 'Upload supporting materials like briefs, decks, PDFs, and relevant links so everyone has context in one place.',
};

const fieldLabels = {
  service: 'Service Topic',
  entryDate: 'Entry Date',
  dueDate: 'Due Date',
  type: 'Type(s) of Work',
  complexity: 'Complexity',
  units: 'Unit',
  dept: 'Department',
  unitLead: 'Lead/Expert',
  teamMember: 'Team Members',
  campaign: 'Campaign',
  status: 'Status',
  actions: 'Background/Response',
  keyMessaging: 'Key Messaging',
  file: 'Comms Material',
};

type ServiceFieldKey = keyof typeof fieldLabels;

type QueryAdderFields = Record<string, any>;
type DropdownTable = Record<string, any>;
type QueryAdderEventHandler = Record<string, any>;
type QueryAdderErrorCols = string[];
type QueryAdderCallback = (value: any, field: string) => void;
type QueryAdderActionHandler = () => void;
type QueryAdderDropHandler = (event: DragEvent<any>) => void;

interface RenderServiceFieldArgs {
  col: ServiceFieldKey;
  fields: QueryAdderFields;
  dropdownTable: DropdownTable;
  callback: QueryAdderCallback;
  currMonth: any;
  requiredInputs: string[];
  errorCols: QueryAdderErrorCols;
  eventHandler: QueryAdderEventHandler;
  files: any[];
  handleDrag: QueryAdderDropHandler;
  handleDrop: QueryAdderDropHandler;
  isDragActive: boolean;
  isUpdatingRow: boolean;
  fullWidth?: boolean;
  helperText?: string;
}

interface ServiceQueryAdderLayoutProps {
  title?: string;
  fields: QueryAdderFields;
  dropdownTable: DropdownTable;
  requiredInputs: string[];
  errorCols: QueryAdderErrorCols;
  eventHandler: QueryAdderEventHandler;
  files: any[];
  handleDrag: QueryAdderDropHandler;
  handleDrop: QueryAdderDropHandler;
  isDragActive: boolean;
  isUpdatingRow: boolean;
  currMonth: any;
  callback: QueryAdderCallback;
  onSubmit: QueryAdderActionHandler;
  onCancel: QueryAdderActionHandler;
  isUpdatingItem?: boolean;
  onDelete?: QueryAdderActionHandler;
}

const serviceMetadataFields: ServiceFieldKey[] = [
  'entryDate',
  'dueDate',
  'type',
  'units',
  'dept',
  'unitLead',
  'teamMember',
  'campaign',
  'complexity',
  'status',
];

const serviceMetadataGridSx = {
  ...queryAdderWideTwoColumnGridSx,
};

function renderServiceField({
  col,
  fields,
  dropdownTable,
  callback,
  currMonth,
  requiredInputs,
  errorCols,
  eventHandler,
  files,
  handleDrag,
  handleDrop,
  isDragActive,
  isUpdatingRow,
  fullWidth = false,
  helperText,
}: RenderServiceFieldArgs) {
  const label = (fieldLabels as Record<string, string>)[col] || col;
  const info = (infoText as Record<string, string>)[col];
  const cell = renderQueryAdderCell({
    col,
    fields,
    dropdownTable,
    callback,
    currMonth,
    requiredInputs,
    errorCols,
    eventHandler,
    files,
    handleDrag,
    handleDrop,
    isDragActive,
    isUpdatingRow,
    hideLabel: true,
    labelText: label,
    fullWidthContainer: true,
  });

  return (
    <QueryAdderFieldGridItem key={col} fullWidth={fullWidth}>
      <FieldLabel label={label} required={requiredInputs.includes(col)} info={info} ui={ui} />
      {cell}
      {helperText ? (
        <Typography
          sx={{ mt: 1, pl: 1.25, fontSize: queryAdderTypeScale.helperText, lineHeight: 1.55, color: ui.helper }}
        >
          {helperText}
        </Typography>
      ) : null}
    </QueryAdderFieldGridItem>
  );
}

function ServiceQueryAdderLayout(props: Readonly<ServiceQueryAdderLayoutProps>) {
  const {
    title,
    fields,
    dropdownTable,
    requiredInputs,
    errorCols,
    eventHandler,
    files,
    handleDrag,
    handleDrop,
    isDragActive,
    isUpdatingRow,
    currMonth,
    callback,
    onSubmit,
    onCancel,
    isUpdatingItem,
    onDelete,
  } = props;
  const [openVerificationModal, setOpenVerificationModal] = useState(false);

  const renderField = (
    col: ServiceFieldKey,
    options: Partial<Pick<RenderServiceFieldArgs, 'fullWidth' | 'helperText'>> = {}
  ) =>
    renderServiceField({
      col,
      fields,
      dropdownTable,
      callback,
      currMonth,
      requiredInputs,
      errorCols,
      eventHandler,
      files,
      handleDrag,
      handleDrop,
      isDragActive,
      isUpdatingRow,
      ...options,
    });

  return (
    <Box className="query-adder-service-layout" sx={{ height: '100%', backgroundColor: ui.white }}>
      <QueryAdderScopedStyles
        scopeClassName=".query-adder-service-layout"
        ui={ui}
        options={{ preserveFieldChrome: true, preserveRichTextChrome: true }}
      />
      <QueryAdderModalShell
        header={
          <QueryAdderStickyHeader
            title={title || serviceAdderConfig.modalTitle}
            infoText={infoText.service}
            icon={serviceAdderConfig.icon}
            ui={ui}
            onClose={onCancel}
            onSubmit={onSubmit}
            onCancel={onCancel}
            onDelete={onDelete ? () => setOpenVerificationModal(true) : undefined}
            isUpdatingItem={isUpdatingItem}
            isUpdatingRow={isUpdatingRow}
            topBorderColor={serviceAdderConfig.logColor}
            titleSx={{ color: ui.title }}
            iconCardSx={{
              backgroundColor: ui.tile,
              color: ui.main,
              borderRadius: '12px',
            }}
          />
        }
        bodySx={{ backgroundColor: ui.white }}
        contentSx={{ pb: 4, backgroundColor: ui.white }}
      >
        <Stack spacing={3}>
          <SectionCard title="Details" icon={<AccountTreeOutlinedIcon sx={{ fontSize: 20 }} />} ui={ui}>
            {renderServiceField({
              col: 'service',
              fields,
              dropdownTable,
              callback,
              currMonth,
              requiredInputs,
              errorCols,
              eventHandler,
              files,
              handleDrag,
              handleDrop,
              isDragActive,
              isUpdatingRow,
              fullWidth: true,
              helperText:
                'This topic will group related Media Interactions, Engagements, and Content linked back to this Service.',
            })}

            <Box sx={{ ...serviceMetadataGridSx, mt: 2 }}>{serviceMetadataFields.map((col) => renderField(col))}</Box>
          </SectionCard>

          <SectionCard title="Notes and Files" icon={<FolderOpenOutlinedIcon sx={{ fontSize: 20 }} />} ui={ui}>
            <Box sx={{ mb: 4 }}>
              {renderServiceField({
                col: 'actions',
                fields,
                dropdownTable,
                callback,
                currMonth,
                requiredInputs,
                errorCols,
                eventHandler,
                files,
                handleDrag,
                handleDrop,
                isDragActive,
                isUpdatingRow,
                fullWidth: true,
              })}
            </Box>

            {renderField('keyMessaging', { fullWidth: true })}

            <Box sx={{ ...queryAdderTwoColumnGridSx, mt: 4 }}>
              <Box sx={{ width: '100%', gridColumn: { xs: 'span 1', lg: 'span 2' }, mb: -0.55 }}>
                <FieldLabel label="Comms Material" info={infoText.file} ui={ui} />
              </Box>

              <Box sx={{ gridColumn: { xs: 'span 1', lg: 'span 2' }, mt: -0.75 }}>
                <QueryAdderUploadDropzone
                  ui={ui}
                  description="Attach briefing notes, messaging documents, PDFs, background materials, or supporting links for this Service."
                  handleDrag={handleDrag}
                  handleDrop={handleDrop}
                  isDragActive={isDragActive}
                >
                  {renderQueryAdderCell({
                    col: 'file',
                    fields,
                    dropdownTable,
                    callback,
                    currMonth,
                    requiredInputs,
                    errorCols,
                    eventHandler,
                    files,
                    handleDrag,
                    handleDrop,
                    isDragActive,
                    isUpdatingRow,
                    hideLabel: true,
                    fullWidthContainer: true,
                    fileRenderMode: 'uploadOnly',
                  })}
                </QueryAdderUploadDropzone>
                {files && files.length > 0 ? (
                  <Box sx={{ mt: 2 }}>
                    {renderQueryAdderCell({
                      col: 'file',
                      fields,
                      dropdownTable,
                      callback,
                      currMonth,
                      requiredInputs,
                      errorCols,
                      eventHandler,
                      files,
                      handleDrag,
                      handleDrop,
                      isDragActive,
                      isUpdatingRow,
                      hideLabel: true,
                      fullWidthContainer: true,
                      fileRenderMode: 'listOnly',
                    })}
                  </Box>
                ) : null}
              </Box>
            </Box>
          </SectionCard>
        </Stack>
      </QueryAdderModalShell>
      {onDelete ? (
        <VerificationModal
          openVerificationModal={openVerificationModal}
          setOpenVerificationModal={setOpenVerificationModal}
          modalTitle="Confirm Deletion"
          modalDescription="Are you sure you want to delete this Service?"
          accentColor={ui.main}
          handleYesClick={() => {
            onDelete();
            setOpenVerificationModal(false);
          }}
        />
      ) : null}
    </Box>
  );
}

export default ServiceQueryAdderLayout;
